import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsArrowReturnRight } from "react-icons/bs";
import DollarInput from "@/components/ui/dollar-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterProps } from "./types";
type ComparisonType = "exact" | "less" | "greater" | "between";

// PostgreSQL integer (int4) max value
const POSTGRES_MAX_INT = 2147483647;
const DEBOUNCE_MS = 500;
function inferDollarState(value: unknown): {
  type: ComparisonType;
  lower: number;
  upper: number;
} {
  if (!Array.isArray(value) || value.length !== 2) return {
    type: "exact",
    lower: 0,
    upper: 0
  };
  const [a, b] = value as [number | null, number | null];
  const aDollars = a != null ? a / 100 : 0;
  const bDollars = b != null ? b / 100 : 0;
  if ((a == null || a === 0) && b != null && b > 0) return {
    type: "less",
    lower: bDollars,
    upper: 0
  };
  if (a != null && a > 0 && (b == null || b >= POSTGRES_MAX_INT)) return {
    type: "greater",
    lower: 0,
    upper: aDollars
  };
  if (a != null && b != null && a === b) return {
    type: "exact",
    lower: aDollars,
    upper: 0
  };
  if (a != null && b != null) return {
    type: "between",
    lower: aDollars,
    upper: bDollars
  };
  return {
    type: "exact",
    lower: 0,
    upper: 0
  };
}
const DollarFilter: React.FC<FilterProps> = t0 => {
  const $ = _c(52);
  const {
    value,
    onChange,
    onSubmit
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = inferDollarState(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const init = t1;
  const [comparisonType, setComparisonType] = useState(init.type);
  const [lowerBound, setLowerBound] = useState(init.lower);
  const [upperBound, setUpperBound] = useState(init.upper);
  const [hasUserInput, setHasUserInput] = useState(false);
  const containerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const lastAppliedRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const focusInput = () => {
        const input = containerRef.current?.querySelector("input");
        if (input) {
          input.focus({
            preventScroll: true
          });
        }
      };
      requestAnimationFrame(focusInput);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== comparisonType) {
    t3 = [comparisonType];
    $[3] = comparisonType;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== onChange) {
    t6 = (type, lower, upper, userInput) => {
      if (!userInput && lower === 0 && upper === 0) {
        return;
      }
      let values = [];
      bb29: switch (type) {
        case "exact":
          {
            if (lower > 0) {
              const amountInCents_1 = Math.round(lower * 100);
              values = [amountInCents_1, amountInCents_1];
            }
            break bb29;
          }
        case "less":
          {
            if (lower > 0) {
              const amountInCents_0 = Math.round(lower * 100);
              values = [0, amountInCents_0];
            }
            break bb29;
          }
        case "greater":
          {
            if (upper > 0) {
              const amountInCents = Math.round(upper * 100);
              values = [amountInCents, POSTGRES_MAX_INT];
            }
            break bb29;
          }
        case "between":
          {
            if (lower > 0 && upper > 0) {
              const lowerInCents = Math.round(lower * 100);
              const upperInCents = Math.round(upper * 100);
              values = [Math.min(lowerInCents, upperInCents), Math.max(lowerInCents, upperInCents)];
            }
          }
      }
      const applyKey = values.length === 2 ? JSON.stringify(values) : "undefined";
      if (lastAppliedRef.current === applyKey) {
        return;
      }
      const allFieldsEmpty = type === "between" ? lower === 0 && upper === 0 : type === "greater" ? upper === 0 : lower === 0;
      if (values.length === 2) {
        lastAppliedRef.current = applyKey;
        onChange(values);
      } else {
        if (userInput && allFieldsEmpty) {
          lastAppliedRef.current = applyKey;
          onChange(undefined);
        }
      }
    };
    $[7] = onChange;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const applyFilter = t6;
  let t7;
  if ($[9] !== applyFilter) {
    t7 = (type_0, lower_0, upper_0) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        applyFilter(type_0, lower_0, upper_0, true);
      }, DEBOUNCE_MS);
    };
    $[9] = applyFilter;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const debouncedApply = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = value_0 => {
      setComparisonType(value_0 as ComparisonType);
      setLowerBound(0);
      setUpperBound(0);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleComparisonTypeChange = t8;
  let t9;
  if ($[12] !== comparisonType || $[13] !== debouncedApply || $[14] !== upperBound) {
    t9 = value_1 => {
      setLowerBound(value_1);
      setHasUserInput(true);
      debouncedApply(comparisonType, value_1, upperBound);
    };
    $[12] = comparisonType;
    $[13] = debouncedApply;
    $[14] = upperBound;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleLowerBoundChange = t9;
  let t10;
  if ($[16] !== comparisonType || $[17] !== debouncedApply || $[18] !== lowerBound) {
    t10 = value_2 => {
      setUpperBound(value_2);
      setHasUserInput(true);
      debouncedApply(comparisonType, lowerBound, value_2);
    };
    $[16] = comparisonType;
    $[17] = debouncedApply;
    $[18] = lowerBound;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleUpperBoundChange = t10;
  let t11;
  if ($[20] !== applyFilter || $[21] !== comparisonType || $[22] !== hasUserInput || $[23] !== lowerBound || $[24] !== upperBound) {
    t11 = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      applyFilter(comparisonType, lowerBound, upperBound, hasUserInput);
    };
    $[20] = applyFilter;
    $[21] = comparisonType;
    $[22] = hasUserInput;
    $[23] = lowerBound;
    $[24] = upperBound;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleBlur = t11;
  let t12;
  if ($[26] !== applyFilter || $[27] !== comparisonType || $[28] !== lowerBound || $[29] !== onSubmit || $[30] !== upperBound) {
    t12 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        applyFilter(comparisonType, lowerBound, upperBound, true);
        onSubmit?.();
      }
    };
    $[26] = applyFilter;
    $[27] = comparisonType;
    $[28] = lowerBound;
    $[29] = onSubmit;
    $[30] = upperBound;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const handleKeyDown = t12;
  let t13;
  if ($[32] !== comparisonType || $[33] !== handleBlur || $[34] !== handleKeyDown || $[35] !== handleLowerBoundChange || $[36] !== handleUpperBoundChange || $[37] !== lowerBound || $[38] !== upperBound) {
    t13 = () => {
      switch (comparisonType) {
        case "exact":
          {
            return <DollarInput value={lowerBound} onValueChange={handleLowerBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" />;
          }
        case "less":
          {
            return <DollarInput value={lowerBound} onValueChange={handleLowerBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" />;
          }
        case "greater":
          {
            return <DollarInput value={upperBound} onValueChange={handleUpperBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" />;
          }
        case "between":
          {
            return <div className="flex w-full items-center gap-2"><DollarInput value={lowerBound} onValueChange={handleLowerBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" /><span className="shrink-0 text-body text-muted-foreground">and</span><DollarInput value={upperBound} onValueChange={handleUpperBoundChange} onBlur={handleBlur} onKeyDown={handleKeyDown} className="!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0" /></div>;
          }
      }
    };
    $[32] = comparisonType;
    $[33] = handleBlur;
    $[34] = handleKeyDown;
    $[35] = handleLowerBoundChange;
    $[36] = handleUpperBoundChange;
    $[37] = lowerBound;
    $[38] = upperBound;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const renderInputs = t13;
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SelectTrigger><SelectValue placeholder="Select comparison type" /></SelectTrigger>;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SelectContent><SelectItem value="exact">is equal to</SelectItem><SelectItem value="less">is less than or equal to</SelectItem><SelectItem value="greater">is greater than or equal to</SelectItem><SelectItem value="between">is between</SelectItem></SelectContent>;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== comparisonType) {
    t16 = <Select value={comparisonType} onValueChange={handleComparisonTypeChange}>{t14}{t15}</Select>;
    $[42] = comparisonType;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <BsArrowReturnRight className="ml-1.5 mr-1 h-5 w-4 flex-shrink-0 text-primary" />;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== renderInputs) {
    t18 = renderInputs();
    $[45] = renderInputs;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t18) {
    t19 = <div className="mt-1 flex items-center">{t17}<div className="mt-1 flex items-center">{t18}</div></div>;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t16 || $[50] !== t19) {
    t20 = <div ref={containerRef}>{t16}{t19}</div>;
    $[49] = t16;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
};
export default DollarFilter;